import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Typography, Divider } from '@material-ui/core';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import uuidv4 from 'uuid/v4';

import { round } from 'utils';
import CloseButton from 'components/CustomButtons/CloseButton';
import SaveButton from 'components/CustomButtons/SaveButton';
import {
  setPayment,
  deletePayment,
  setShowPaymentInput,
  setPaymentSelected,
} from 'actions/Payments';
import {
  getLastPayment,
  getShowPaymentInput,
  getPaymentsCurrency,
} from 'reducers/Payments';
import { tapToPayCaption } from 'paymentIntegrations/wrapper/constants';

const formatNumber = (number: string) => {
  let formatedNumber = number;
  formatedNumber = formatedNumber.replace(/[,]/g, '.');
  formatedNumber = formatedNumber.replace(/[^0-9-.]/g, '');
  if (
    formatedNumber.match(/^(-{0,1})([0-9]{0,1}|[1-9]{1}[0-9]*).{0,1}[0-9]*$/g)
  ) {
    if (formatedNumber.match(/[-]/g)) {
      formatedNumber = `-${formatedNumber.replace(/[-]/g, '')}`;
    }
    if (formatedNumber.match(/^[.]/g)) {
      formatedNumber = formatedNumber.replace(/^[.]/g, '0.');
    }
    if (formatedNumber.match(/^[-]+[.]/g)) {
      formatedNumber = formatedNumber.replace(/^[-]+[.]/g, '-0.');
    }
    if (formatedNumber.match(/^[0]+[1-9]/g)) {
      formatedNumber = formatedNumber.replace(/^[0]/g, '');
    }
    return formatedNumber;
  }
  return '';
};

function getPaymentKey(paymentType: string, currency: string): string {
  if (['CASH', 'CHANGE'].includes(paymentType)) {
    return `${currency}-cash`;
  }
  if (['TIP', 'CHECK'].includes(paymentType)) {
    return paymentType;
  }
  return uuidv4();
}

const PaymentInput: React.FC = () => {
  const { t } = useTranslation('payment');
  const [amount, setAmount] = useState('');
  const { lastPayment, lastKey } = useSelector(getLastPayment);
  const { paymentType, isTapToPay = false, paymentData } = useSelector(
    getShowPaymentInput,
  );
  const { code: currency, rate } = useSelector(getPaymentsCurrency);

  const dispatch: ThunkDispatch<unknown, unknown, Action> = useDispatch();

  const handleSave = async () => {
    const amountValue = amount;
    if (
      amountValue &&
      !Number.isNaN(Number(amountValue)) &&
      Number(amountValue) > 0
    ) {
      if (lastPayment.amount === '0.00') {
        dispatch(deletePayment({ key: lastKey }));
      }
      const payment =
        paymentType === 'CARD' && isTapToPay
          ? {
              key: getPaymentKey(paymentType, currency),
              type: paymentType,
              caption: tapToPayCaption,
              amount: round(amountValue),
              currencyCode: currency,
              currencyRate: Number(rate),
              attributes: {
                isTapToPayPayment: true,
                hostReferenceID: uuidv4(),
              },
            }
          : {
              key: getPaymentKey(paymentType, currency),
              amount: round(amountValue),
              caption: paymentType,
              type: paymentType,
              currencyCode: currency,
              currencyRate: Number(rate),
              attributes: {
                hostReferenceID: uuidv4(),
              },
            };

      await dispatch(setPayment({ ...payment, ...paymentData }));

      await dispatch(setShowPaymentInput(false, paymentType));
      // Deselect the payment because otherwise it stays selected
      dispatch(setPaymentSelected(''));
    }
  };

  return (
    <Box padding="1.5rem">
      <Box display="flex" alignItems="center" marginBottom="1rem">
        <Typography variant="h5">
          {t('bill.inputs.paymentAmount', { context: 'title' })}
        </Typography>
        <Box display="flex" justifyContent="flex-end" flex={1}>
          <SaveButton color="secondary" action={handleSave} />
          <CloseButton
            action={() => {
              dispatch(setShowPaymentInput(false, null));
            }}
          />
        </Box>
      </Box>
      <Divider style={{ margin: '0.75rem 0' }} />
      <TextField
        label={t('bill.inputs.paymentAmount', {
          context: 'placeholder',
        })}
        fullWidth
        variant="outlined"
        size="small"
        value={amount}
        onChange={e => setAmount(formatNumber(e.target.value))}
        onFocus={event => event.preventDefault()}
        autoFocus
      />
    </Box>
  );
};

export default PaymentInput;
